// Overlap detection between placed products. Imports only calculations.ts,
// so the snapping modules can call it without importing cabinetPosition.ts.
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    isAdjustableLeg,
} from 'components/customer/RoomPlanner/helpers/calculations';

// The cabinet's box without its adjustable legs, which would otherwise reach
// the floor and take in anything standing under the carcase.
const boundsExcludingAdjustableLegs = (group: THREE.Object3D) => {
    const bounds = new THREE.Box3();
    const meshBounds = new THREE.Box3();

    group.updateWorldMatrix(true, true);
    group.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh || isAdjustableLeg(child)) return;
        meshBounds.setFromObject(child);
        if (!meshBounds.isEmpty()) bounds.union(meshBounds);
    });

    return bounds.isEmpty() ? new THREE.Box3().setFromObject(group) : bounds;
};

const getCollisionBoundingBox = (mesh: CabinetMesh): THREE.Box3 => {
    if (mesh.userData?.isPlaceholder) {
        const box = computeWorldBoundsExcludingAnimatedDoorParts(mesh);
        if (
            !box.isEmpty() &&
            Number.isFinite(box.min.x) &&
            Number.isFinite(box.max.x)
        ) {
            return box;
        }
    }
    return new THREE.Box3().setFromObject(mesh);
};

const isSameLayoutItem = (a: CabinetMesh, b: CabinetMesh): boolean => {
    if (a === b || a.uuid === b.uuid) return true;
    // Cabinet numeric ids can collide with placeholder ids (e.g. both `1`).
    // Only treat matching ids as the same item when both are placeholders
    // or both are cabinets.
    return (
        a.userData.id === b.userData.id &&
        Boolean(a.userData.isPlaceholder) === Boolean(b.userData.isPlaceholder)
    );
};

// Kicker frames and inner-drawer-only products set ignoreOverlap so they can
// share a footprint. A floor product or placeholder still collides with one,
// and a no-bottom product still collides with a floor product.
const countsDespiteIgnoreOverlap = (
    cabinet: CabinetMesh,
    other: CabinetMesh
): boolean => {
    if (!other.userData?.ignoreOverlap) return true;

    const otherOnFloor = Boolean(
        other.userData.isFloorProduct || other.userData.isPlaceholder
    );

    return (
        (Boolean(
            cabinet.userData.isFloorProduct || cabinet.userData.isPlaceholder
        ) &&
            otherOnFloor) ||
        (Boolean(cabinet.userData.noBottom) && otherOnFloor)
    );
};

export const checkCabinetOverlap = (
    cabinet: CabinetMesh,
    targetPos: THREE.Vector3,
    otherCabinets: CabinetMesh[],
    buffer = 0,
    overlapOverride = false,
    // Measure other cabinets without their adjustable legs (R-8), so a
    // kick-length panel under a legged cabinet doesn't count as overlapping.
    ignoreAdjustableLegs = false
): CabinetMesh[] | null => {
    // Create a temporary box for the cabinet at the target position
    const tempBox = getCollisionBoundingBox(cabinet);
    const offset = new THREE.Vector3().subVectors(targetPos, cabinet.position);
    tempBox.translate(offset);
    const overlappingCabinets: CabinetMesh[] = [];

    // Check against all other cabinets
    for (const otherCabinet of otherCabinets) {
        if (isSameLayoutItem(otherCabinet, cabinet)) continue; // Skip self

        const otherBox = otherCabinet.userData?.isPlaceholder
            ? getCollisionBoundingBox(otherCabinet)
            : ignoreAdjustableLegs
              ? boundsExcludingAdjustableLegs(otherCabinet)
              : new THREE.Box3().setFromObject(otherCabinet);

        // Add a small buffer to prevent exact touching
        otherBox.expandByScalar(buffer);

        if (
            tempBox.intersectsBox(otherBox) &&
            (overlapOverride ||
                countsDespiteIgnoreOverlap(cabinet, otherCabinet))
        ) {
            overlappingCabinets.push(otherCabinet);
        }
    }

    if (overlappingCabinets.length > 0) {
        return overlappingCabinets;
    }

    return null;
};

const isCollisionBodyMesh = (
    child: THREE.Object3D,
    root: THREE.Object3D
): child is THREE.Mesh => {
    if (!(child as THREE.Mesh).isMesh || !child.visible) return false;
    if (child.userData?.ignoreOverlap) return false;
    if (child.name === 'Adjustable_leg') return false;

    let node: THREE.Object3D | null = child;
    while (node && node !== root) {
        if (node.type === 'ArrowHelper') return false;
        const id = String(node.userData?.id ?? '');
        const name = node.name ?? '';
        if (
            id.includes('arrowGroup') ||
            id.includes('Dimension') ||
            name === 'cabinetMovementArrows' ||
            name.includes('Measurement')
        ) {
            return false;
        }
        node = node.parent;
    }
    return true;
};

const collectCollisionBodyMeshes = (root: THREE.Object3D): THREE.Mesh[] => {
    const meshes: THREE.Mesh[] = [];
    root.traverse((child) => {
        if (isCollisionBodyMesh(child, root)) {
            meshes.push(child);
        }
    });
    return meshes;
};

const getCollisionBodyBox = (root: THREE.Object3D): THREE.Box3 => {
    const box = new THREE.Box3();
    box.makeEmpty();
    for (const mesh of collectCollisionBodyMeshes(root)) {
        box.union(new THREE.Box3().setFromObject(mesh));
    }
    if (box.isEmpty()) {
        return new THREE.Box3().setFromObject(root);
    }
    return box;
};

/**
 * A snapped placeholder sits flush (overlap ≈ 0). That touch is not a
 * collision. Cabinets use the keyboard escape inset instead.
 */
const PLACEHOLDER_CONTACT_TOLERANCE = 1;

const axisOverlap = (
    minA: number,
    maxA: number,
    minB: number,
    maxB: number
): number => Math.min(maxA, maxB) - Math.max(minA, minB);

const doProductBodiesOverlap = (
    moving: CabinetMesh,
    other: CabinetMesh,
    buffer: number
): boolean => {
    const movingBox = getCollisionBodyBox(moving);
    const otherBox = getCollisionBodyBox(other);
    if (movingBox.isEmpty() || otherBox.isEmpty()) return false;

    if (buffer !== 0) otherBox.expandByScalar(buffer);
    if (!movingBox.intersectsBox(otherBox)) return false;
    if (!moving.userData?.isPlaceholder) return true;

    const xOverlap = axisOverlap(
        movingBox.min.x,
        movingBox.max.x,
        otherBox.min.x,
        otherBox.max.x
    );
    const zOverlap = axisOverlap(
        movingBox.min.z,
        movingBox.max.z,
        otherBox.min.z,
        otherBox.max.z
    );

    return (
        xOverlap > PLACEHOLDER_CONTACT_TOLERANCE &&
        zOverlap > PLACEHOLDER_CONTACT_TOLERANCE
    );
};

/**
 * Visible-body overlap at a target position (no dimension arrows / overlays).
 * Keyboard uses this so red + blocking match the fridge/oven mesh, not the
 * measurement helpers around it. `buffer` matches `checkCabinetOverlap`: a
 * negative value shrinks the other body so a flush snap can still move.
 * Pairs that include a placeholder use 0, so a shallow cabinet↔placeholder
 * hit is still seen and a placeholder elsewhere keeps the cabinet inset.
 * `ignoreOverlap` follows `checkCabinetOverlap`: kicker frames and
 * inner-drawer-only products share a footprint, and still collide when
 * the floor-product rules apply.
 * @param {CabinetMesh} cabinet Mesh being moved
 * @param {THREE.Vector3} targetPos Position to test
 * @param {CabinetMesh[]} otherCabinets Other layout items to test against
 * @param {number} [buffer=0] Inset applied to cabinet↔cabinet pairs
 * @return {CabinetMesh[]|null} Overlapping items, or null if none
 */
export const checkCabinetBodyOverlap = (
    cabinet: CabinetMesh,
    targetPos: THREE.Vector3,
    otherCabinets: CabinetMesh[],
    buffer = 0
): CabinetMesh[] | null => {
    const previous = cabinet.position.clone();
    cabinet.position.copy(targetPos);
    cabinet.updateMatrixWorld(true);

    const overlapping: CabinetMesh[] = [];
    for (const other of otherCabinets) {
        if (isSameLayoutItem(other, cabinet)) continue;
        if (!countsDespiteIgnoreOverlap(cabinet, other)) continue;
        const pairBuffer =
            cabinet.userData?.isPlaceholder || other.userData?.isPlaceholder
                ? 0
                : buffer;
        if (doProductBodiesOverlap(cabinet, other, pairBuffer)) {
            overlapping.push(other);
        }
    }

    cabinet.position.copy(previous);
    cabinet.updateMatrixWorld(true);

    return overlapping.length > 0 ? overlapping : null;
};
